/**
 * @changelog
 *
 * 2026-10-06 (0.2.0) (jhoward)
 *   - Include the selected prize's date and time in the prize description, formatted as in the prize picker.
 *
 * 2026-09-08 (0.1.0) (jhoward)
 *   - Add src\helpers\getOfferRedemption.ts to describe a redeemed offer in words - which of the three redeemed states
 *     it is in, who redeemed it, and which prize was taken - so the card badge, the detail banner and the offers
 *     filter all read from one place.
 */

import type { OpcOffer } from '@/defs/api/OpcOffer';
import getReadablePrizeDate from '@/helpers/getReadablePrizeDate';
import getSelectedPrize from '@/helpers/getSelectedPrize';
import isOfferRedeemed from '@/helpers/isOfferRedeemed';
import useAppStore from '@/store';

export interface OfferRedemption {
  isRedeemed: boolean;
  //'Redeemed' | 'Reserved' | 'Checked In' - the three states isOfferRedeemed() treats as redeemed.
  label: string;
  //Material Icons ligature for the badge.
  icon: string;
  //Who acted on the offer, already phrased for display ('you', 'Jane Smith'). Null when it cannot be determined.
  redeemedBy: string | null;
  //The prize taken, already phrased ('2 x Concert Hall'). Null for offers that carry no prize selection.
  prize: string | null;
  //Single line combining the above, for a badge tooltip.
  summary: string;
}

const STATUS_LABELS: { [key: string]: { label: string; icon: string } } = {
  REDEEMED: { label: 'Redeemed', icon: 'check_circle' },
  RESERVED: { label: 'Reserved', icon: 'event_available' },
  'CHECKED-IN': { label: 'Checked In', icon: 'how_to_reg' },
};

/**
 * The offer only carries the patron it belongs to, plus a flag for whether the joint holder was the one who took it.
 * That is enough for the three cases a guest actually needs told apart: they did it, the other person on their own
 * account did it, or it happened on a linked account they can see.
 */
function getRedeemedBy(offer: OpcOffer): string | null {
  const appStore = useAppStore();
  const accountId = appStore.state.user?.account_id;

  //A linked account's offer - CASINO_PATRON_FULL_NAME is whose it is.
  if (accountId && offer.CASINO_PATRON_ID !== accountId) {
    return offer.CASINO_PATRON_FULL_NAME || null;
  }

  if ('YES' === offer.WAS_OFFER_REDEEMED_BY_JOINT) {
    //Named when account info happens to be loaded; the offers list does not fetch it, so fall back to the relationship.
    return appStore.account.info?.CASINO_PATRON_LINKED_FULL_NAME || 'the joint account holder';
  }

  return accountId ? 'you' : null;
}

function getPrizeDescription(offer: OpcOffer): string | null {
  const prize = getSelectedPrize(offer);
  if (!prize?.CASINO_PRIZE_NAME) {
    return null;
  }

  const quantity = offer.PRIZE_QUANTITY_RESERVED;

  const name = quantity && quantity > 1 ? quantity + ' × ' + prize.CASINO_PRIZE_NAME : prize.CASINO_PRIZE_NAME;

  //The date tells apart prizes with the same name, as it did in the picker when it was chosen.
  return prize.PRIZE_START_TS ? name + ' · ' + getReadablePrizeDate(prize.PRIZE_START_TS) : name;
}

export default function getOfferRedemption(offer: OpcOffer): OfferRedemption {
  if (!isOfferRedeemed(offer)) {
    return { isRedeemed: false, label: '', icon: '', redeemedBy: null, prize: null, summary: '' };
  }

  const status = STATUS_LABELS[offer.OFFER_ACCEPT_STATUS] ?? STATUS_LABELS.REDEEMED;
  const redeemedBy = getRedeemedBy(offer);
  const prize = getPrizeDescription(offer);

  let summary = status.label;
  if (redeemedBy) {
    summary += ' by ' + redeemedBy;
  }
  if (prize) {
    summary += ' — ' + prize;
  }

  return {
    isRedeemed: true,
    label: status.label,
    icon: status.icon,
    redeemedBy,
    prize,
    summary,
  };
}
